import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Fragment } from "react";
import s2Fund from "../../assets/s2-fund.mp4";
import s2Grow from "../../assets/s2-grow.mp4";
import s2Pay from "../../assets/s2-pay.mp4";
import { GrainLowOverlay } from "../../lib/grain-low-overlay";
import { GREIGE_GRAIN_SURFACE_CLASS } from "../../lib/greige-grain-surface";
import { LANDING_CONTAINER_CLASS, LANDING_SECTION_PAD_CLASS, LANDING_STICKY_BELOW_NAV_CLASS } from "../../lib/layout";
import { useSectionTwoAccordion } from "./use-section-two-accordion";
import { useSectionTwoHandoffParallax } from "./use-section-two-handoff";

const FEATURE_ASPECT_CLASS = "block aspect-[16/10] w-full select-none object-cover";

type TFeatureRow = {
	index: string;
	title: string;
	description: string;
	descriptionLead?: string;
	asset: string;
	assetAlt: string;
};

function FeatureRowDescription({ row, className }: { row: TFeatureRow; className: string }) {
	return (
		<p className={className}>
			{row.descriptionLead ? (
				<>
					<span className="font-semibold">{row.descriptionLead}</span> {row.description}
				</>
			) : (
				row.description
			)}
		</p>
	);
}

const FEATURE_ROWS: readonly TFeatureRow[] = [
	{
		index: "01",
		title: "Fund your inventory",
		descriptionLead: "A new type of credit facility - built for consumer brands.",
		description:
			"Choose the amount, repayment type and length of term that match your inventory lead-time and sell-through rate.",
		asset: s2Fund,
		assetAlt: "Triffin funding dashboard on a laptop",
	},
	{
		index: "02",
		title: "Pay suppliers in one click",
		descriptionLead: "Reduce the time and cost of processing payments.",
		description: "Streamline finance ops, align payments with cash flow and supplier intelligence.",
		asset: s2Pay,
		assetAlt: "Paying a supplier from a phone",
	},
	{
		index: "03",
		title: "Grow with cash flow clarity",
		descriptionLead: "Finance tools that work the way you do.",
		description: "Replace manual finance tasks with integrations, automation and AI, all ",
		asset: s2Grow,
		assetAlt: "Triffin payment card",
	},
];

function FeatureVideo({ src, alt, className }: { src: string; alt: string; className: string }) {
	return (
		<video
			className={className}
			autoPlay
			loop
			muted
			playsInline
			preload="auto"
			aria-label={alt}
			ref={(el) => {
				if (el) el.muted = true;
			}}
		>
			<source src={src} type="video/mp4" />
		</video>
	);
}

/**
 * Section 2 — numbered feature rows. Mobile: a plain separated list. Desktop: a pinned
 * accordion below the site header — all rows stay visible, and scrolling collapses the
 * open row while expanding the next. After 03 settles, one leftover viewport keeps the
 * stage pinned while S3 slides over it.
 */
export function SectionTwo() {
	return (
		<section
			className={cn(GREIGE_GRAIN_SURFACE_CLASS, "w-full", LANDING_SECTION_PAD_CLASS)}
			aria-label="What Triffin does"
		>
			<GrainLowOverlay />
			<div className="relative z-[1]">
				<MobileFeatureList />
				<DesktopFeatureAccordion />
			</div>
		</section>
	);
}

function MobileFeatureList() {
	return (
		<div className="flex flex-col lg:hidden">
			{FEATURE_ROWS.map((row) => (
				<div key={row.index} className="border-t cc-border-grey-800 py-10">
					<div className="flex items-start justify-between gap-4">
						<h3 className="m-0 text-[32px] leading-[1.3] tracking-[-0.03em] font-normal text-teal-500">{row.title}</h3>
						<span className="text-[32px] leading-[1.3] tracking-[-0.03em] font-normal text-grey-900">{row.index}</span>
					</div>
					<FeatureRowDescription
						row={row}
						className="m-0 mt-6 max-w-[52ch] text-landing-section-body font-normal text-teal-500"
					/>
					<div className="mt-6">
						<FeatureVideo src={row.asset} alt={row.assetAlt} className={FEATURE_ASPECT_CLASS} />
					</div>
				</div>
			))}
		</div>
	);
}

function DesktopFeatureAccordion() {
	const { runwayRef, grows } = useSectionTwoAccordion();
	const handoffY = useSectionTwoHandoffParallax();

	return (
		<div ref={runwayRef} className="max-lg:hidden lg:h-[400svh]" data-section-two-runway>
			{/* --landing-nav-height comes from the landing root, which measures the host nav. */}
			<div className={cn(LANDING_CONTAINER_CLASS, LANDING_STICKY_BELOW_NAV_CLASS, "flex flex-col")}>
				<motion.div className="flex min-h-0 flex-1 flex-col" style={{ y: handoffY }}>
					{FEATURE_ROWS.map((row, index) => (
						<Fragment key={row.index}>
							<div className="grid flex-none grid-cols-2 items-baseline gap-12 border-t cc-border-grey-800 py-8">
								<div className="flex items-baseline gap-12">
									<span className="text-[32px] leading-[1.3] tracking-[-0.03em] font-normal text-grey-900">
										{row.index}
									</span>
									<h3 className="m-0 text-[32px] leading-[1.3] tracking-[-0.03em] font-normal text-teal-500">
										{row.title}
									</h3>
								</div>
								<FeatureRowDescription
									row={row}
									className="m-0 max-w-[80%] text-landing-section-body font-normal text-teal-500"
								/>
							</div>
							<motion.div className="min-h-0 overflow-hidden" style={{ flexGrow: grows[index], flexBasis: 0 }}>
								<div className="grid h-full grid-cols-2 gap-12">
									<div className="col-start-2 min-h-0 pb-[calc(1.5rem*2.2)]">
										<FeatureVideo src={row.asset} alt={row.assetAlt} className={FEATURE_ASPECT_CLASS} />
									</div>
								</div>
							</motion.div>
						</Fragment>
					))}
				</motion.div>
			</div>
		</div>
	);
}
